import { useQuery } from '@tanstack/react-query';
import type { InventoryItemView, InventoryStatusEnum } from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

export interface InventoryQueryParams {
  page?: number;
  size?: number;
  locationId?: number | null;
  keyword?: string;
  status?: InventoryStatusEnum | null;
}

export interface InventoryPage {
  items: ReadonlyArray<InventoryItemView>;
  meta: ApiMeta;
}

const cleanQuery = (params: InventoryQueryParams) => {
  const out: Record<string, unknown> = {};
  out.page = Math.max(0, (params.page ?? 1) - 1);
  if (params.size) out.size = params.size;
  if (params.locationId) out.locationId = params.locationId;
  if (params.keyword) out.keyword = params.keyword;
  if (params.status) out.status = params.status;
  return out;
};

export const inventoryQueryKey = (params: InventoryQueryParams) =>
  ['admin', 'inventory', params] as const;

export const useInventoryQuery = (params: InventoryQueryParams) =>
  useQuery<InventoryPage>({
    queryKey: inventoryQueryKey(params),
    queryFn: async () => {
      const [data, meta] = await apiClient.request('get', '/admin/v1/inventory', {
        params: { query: cleanQuery(params) },
      });
      return { items: (data as InventoryItemView[]) ?? [], meta };
    },
  });
